'use client'

import React from 'react'
import AnimatedOrb from './AnimatedOrb'

/**
 * BottomChatNav
 * A glassy bottom navigation + chat trigger for the phone mockup with four primary affordances:
 * - Home, Tomorrow (leaf), Flow (waves) inside a rounded glass nav
 * - A separate orb button on the right
 * It supports a basic set of states now and is structured to expand to the four-state flow later.
 */

export type BottomNavState =
  | 'nav'            // default expanded navigation
  | 'nav-notify'     // navigation with notification glow
  | 'collapsed'      // collapsed with hamburger
  | 'collapsed-notify' // collapsed with glow
  | 'chat'           // text field expanded
  | 'chat-notify'    // text field with glow
  | 'chat-input'     // input field with send icon (no orb/text)

export type NavItem = 'home' | 'tomorrow' | 'flow'

interface BottomChatNavProps {
  state?: BottomNavState
  activeNav?: NavItem  // Which nav item is currently active
  onOrbClick?: () => void
  onHamburgerClick?: () => void
  // When true and state is chat/chat-notify, hide the message text for staged animations
  hideChatText?: boolean
  // Optional style overrides for the chat text element (used for timed fade-ins)
  chatTextStyle?: React.CSSProperties
  collapsedText?: string
  chatPreviewText?: string
  navLabels?: Partial<Record<NavItem, string>>
}

const IconHome = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

const IconLeaf = () => (
  <svg width="16" height="16" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.40002 2.771C8.54121 2.771 11.1451 5.13023 11.6221 8.21644C12.6797 7.13474 14.1395 6.46596 15.7506 6.46596H19.8009V8.7753C19.8009 12.0915 17.1817 14.7796 13.9505 14.7796H11.7004V19.3983H9.90029V12.0084H8.10019C4.62058 12.0084 1.7998 9.11339 1.7998 5.54222V2.771H5.40002ZM18.0008 8.31343H15.7506C13.514 8.31343 11.7004 10.1748 11.7004 12.4703V12.9321H13.9505C16.1872 12.9321 18.0008 11.0708 18.0008 8.7753V8.31343ZM5.40002 4.61848H3.59991V5.54222C3.59991 8.09266 5.61514 10.1609 8.10019 10.1609H9.90029V9.23717C9.90029 6.68673 7.88507 4.61848 5.40002 4.61848Z" fill="currentColor"/>
  </svg>
)

const IconWaves = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M3 12c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M3 16c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

const IconHamburger = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Paper plane pointing upward */}
    <path d="M12 4l7 14-7-3-7 3 7-14z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
    <path d="M12 4v7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

export default function BottomChatNav({
  state = 'nav',
  activeNav,
  onOrbClick,
  onHamburgerClick,
  hideChatText,
  chatTextStyle,
  collapsedText = 'Here if you need me!',
  chatPreviewText = 'Hey, I spotted an opportunity for you.',
  navLabels,
}: BottomChatNavProps) {
  const isNotify = state === 'nav-notify' || state === 'collapsed-notify' || state === 'chat-notify'
  const isCollapsed = state === 'collapsed' || state === 'collapsed-notify'
  const isChat = state === 'chat' || state === 'chat-notify' || state === 'chat-input'

  // Unified sizing so the group dimensions are consistent across screens
  const BAR_WIDTH = 170
  const CIRCLE_SIZE = 44

  // Shared glass styles for the nav container
  const glassStyles: React.CSSProperties = {
    backgroundColor: 'rgba(0,0,0,0.01)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 17.2449,
    border: '1px solid rgba(255,255,255,0.9)',
    boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
  }

  // Notify styling for orb only (screen 2): blue-grey blurred drop shadow + sky-blue inner shadow
  const orbNotifyShadow = isNotify
    ? [
        '0 14px 36px rgba(93, 151, 209, 0.45)', // outer blue-grey blur (reduced)
        '0 0 28px rgba(93, 151, 209, 0.35)',    // halo (reduced)
        'inset 0 0 18px rgba(136, 199, 219, 0.65)', // inner sky-blue (reduced)
        'inset 0 0 8px rgba(168, 225, 223, 0.5)'    // subtle tiffany inner (reduced)
      ].join(', ')
    : undefined

  // Notify styling for chat bar when text field is expanded
  const chatNotifyShadow = isNotify
    ? '0 10px 28px rgba(93, 151, 209, 0.35), 0 0 24px rgba(136, 199, 219, 0.45)'
    : undefined

  if (isCollapsed) {
    // Collapsed: hamburger on left, small rounded speech bar in the middle
    return (
      <div className="w-full flex items-end gap-3 px-1 pb-0">
        <div className="shrink-0 w-[44px] h-[44px] rounded-full flex items-center justify-center text-night/80 bg-white/60 shadow" style={{ filter: isNotify ? 'drop-shadow(0 10px 22px rgba(168,225,223,0.7))' : undefined }} onClick={onHamburgerClick}>
          <IconHamburger />
        </div>
        <div className="flex-1">
          <div className="h-[44px] px-4 flex items-center text-night/80" style={{ ...glassStyles, boxShadow: glassStyles.boxShadow + (chatNotifyShadow ? `, ${chatNotifyShadow}` : '') }}>
            <div className="w-6 h-6 mr-2">
              <AnimatedOrb size={22} className="w-full h-full" />
            </div>
            <div className="text-[13px] leading-none">{collapsedText}</div>
          </div>
        </div>
      </div>
    )
  }

  if (state === 'chat' || state === 'chat-notify' || state === 'chat-input') {
    // Chat text field expanded: nav collapsed on left as a circular glass button; message bar stretched to align with orb width in screen 2
    const isNotifyChat = state === 'chat-notify'
    const isInput = state === 'chat-input'
    return (
      <div className="w-full flex justify-center pb-0">
        <div className="flex items-center gap-3 px-1">
          {/* Hamburger circle same shape as orb container */}
          <button
            className="shrink-0 grid place-items-center text-night/80"
            style={{
              width: CIRCLE_SIZE,
              height: CIRCLE_SIZE,
              borderRadius: 9999,
              ...glassStyles,
            }}
            onClick={onHamburgerClick}
          >
            <IconHamburger />
          </button>
          {/* Message bar - fixed width similar to nav bar, with notify glow when applicable or input variant */}
          <div
            className="flex items-center h-[44px] px-4"
            style={{
              width: BAR_WIDTH,
              borderRadius: glassStyles.borderRadius as number,
              backgroundColor: (glassStyles as any).backgroundColor,
              backdropFilter: (glassStyles as any).backdropFilter,
              WebkitBackdropFilter: (glassStyles as any).WebkitBackdropFilter,
              border: (glassStyles as any).border,
              boxShadow: isInput ? 'none' : `${glassStyles.boxShadow}${isNotifyChat && chatNotifyShadow ? `, ${chatNotifyShadow}` : ''}`,
            }}
          >
            {isInput ? (
              <>
                <div className="flex-1" />
                <button className="ml-3 text-night/80">
                  <IconSend />
                </button>
              </>
            ) : (
              <>
                <div className="w-5 h-5 mr-2">
                  <AnimatedOrb size={20} className="w-full h-full" />
                </div>
                {hideChatText ? (
                  <div className="text-[10px] leading-snug text-night whitespace-normal break-words opacity-0 select-none" style={chatTextStyle}>{chatPreviewText}</div>
                ) : (
                  <div className="text-[10px] leading-snug text-night whitespace-normal break-words" style={chatTextStyle}>{chatPreviewText}</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Default: expanded navigation with three items and orb on the right
  // Active nav item color: #5D97D1 (blue-grey)
  const activeColor = '#5D97D1'
  
  return (
    <div className="w-full flex justify-center pb-0">
      <div className="flex items-center gap-3 px-1">
        <div className="h-[44px] px-5 flex items-center justify-center gap-6 text-night/90" style={{ ...glassStyles, width: BAR_WIDTH }}>
          <div className="flex flex-col items-center gap-0.5" style={{ color: activeNav === 'home' ? activeColor : undefined }}>
            <IconHome />
            <div className="text-[10px] leading-none">{navLabels?.home ?? 'Home'}</div>
          </div>
          <div className="flex flex-col items-center gap-0.5" style={{ color: activeNav === 'tomorrow' ? activeColor : undefined }}>
            <IconLeaf />
            <div className="text-[10px] leading-none">{navLabels?.tomorrow ?? 'Tomorrow'}</div>
          </div>
          <div className="flex flex-col items-center gap-0.5" style={{ color: activeNav === 'flow' ? activeColor : undefined }}>
            <IconWaves />
            <div className="text-[10px] leading-none">{navLabels?.flow ?? 'Flow'}</div>
          </div>
        </div>
        <button
          className="shrink-0 grid place-items-center"
          style={{
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            borderRadius: 9999,
            ...glassStyles,
            boxShadow: glassStyles.boxShadow + (orbNotifyShadow ? `, ${orbNotifyShadow}` : ''),
            transition: 'box-shadow 800ms ease',
            willChange: 'box-shadow',
          }}
          onClick={onOrbClick}
        >
          <div className="w-5 h-5">
            <AnimatedOrb size={20} className="w-full h-full" />
          </div>
        </button>
      </div>
    </div>
  )
}

